import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getActiveOwners,
  getGridDataByModuleKey,
  getProductDropdown,
  getProductList,
  getProductReferenceLookupByKey,
} from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import Loader from '../../shared/component/Loader';
import useProductColumnDefs from './useProductColumnDefs';
import CustomStatusBar from '../sharedChild/grid/customStatusBar';
import CustomSelectedRowCount from '../sharedChild/grid/customSelectedRowCount';
import gridOptionsConfig from '../sharedChild/grid/gridOptionsConfig';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { dropdownPayload, productInputData, productReferenceLookup } from '../../shared/constants';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import FilterHandler from '../sharedChild/grid/filterHandler';

const ProductListing = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'productList');
  const navigate = useNavigate();
  const gridRef = useRef();
  const popupParent = useRef(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridParams, setGridParams] = useState(null);
  const rowData = useSelector(getProductList);
  const [loading, setLoading] = useState(storeLoading);
  const [selectedTheme, setSelectedTheme] = useState('balham');
  const location = useLocation();
  const dispatch = useDispatch();
  const gridContainerRef = useRef();
  const gridOptions = gridOptionsConfig('productList', navigate);
  const gridData = useSelector(getGridDataByModuleKey('productList'));
  const owner = useSelector(getActiveOwners);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useFetchIfNeeded(getProductList, fetchDataAndUpdateStore, {
    key: 'productList',
    inputData: productInputData,
  });

  useFetchIfNeeded(getProductDropdown, fetchDataAndUpdateStore, {
    key: 'productDropdown',
    inputData: dropdownPayload,
  });

  useFetchIfNeeded(getProductReferenceLookupByKey, fetchDataAndUpdateStore, {
    key: 'productReferenceLookup',
    inputData: productReferenceLookup,
  });

  useEffect(() => {
    if (gridContainerRef.current) {
      gridContainerRef.current.style.setProperty(
        'height',
        'calc(100vh - (0px + 138px))',
        'important'
      );
    }
  }, []);

  const handleClickOpenEditForm = (paramsValue) => {
    navigate('/products/editProduct', {
      state: paramsValue,
    });
  };

  const handleClickDelete = (paramsValue) => {
    setLoading(true);
    let data;
    data = {
      id: [paramsValue.id],
    };
    apiServices
      .deleteProduct(data)
      .then(async (response) => {
        if (response.status === 200) {
          toast.success('Product Deleted Successfully!', {
            position: 'top-right',
          });
          if (paramsValue.id === undefined) {
            document.getElementById('massDeleteModal').click();
          }
          const columnName = 'prodcutName';
          const orderType = 'desc';
          const data = {
            pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
            pageNumber: 1,
            columnName,
            orderType,
            filterJson: null,
          };

          try {
            // ✅ Await the dispatch before navigation
            await dispatch(fetchDataAndUpdateStore({ key: 'productList', inputData: data }));
          } catch (error) {
            toast.error('Something went wrong while updating the list.', {
              position: 'top-right',
            });
          }
          setLoading(false);
        } else {
          setLoading(false);
          toast.error('Error deleting record', {
            position: 'top-right',
          });
        }
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
        setLoading(false);
      });
  };

  const columnDefs = useProductColumnDefs({
    handleClickDelete,
    handleClickOpenEditForm,
    owner
  });

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      enableRowGroup: true,
    }),
    []
  );

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressPivotMode: true, // 👈 Hide Pivot Mode toggle
            suppressRowGroups: false, // ✅ Allow row grouping
            suppressValues: false, // ✅ Allow aggregation if needed
            suppressPivots: true, // 👈 Hide the pivot section for columns
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: 'left',
      defaultToolPanel: 'none', //show off filter tab bydefault.
    };
  }, []);

  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;

    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({
        state: columnOrderFromStore,
        applyOrder: true,
      });
    } else {
      // apply default column visibility if no saved state
      const fieldsToHide = [];

      const defaultState = params.columnApi.getColumnState().map((col) => ({
        ...col,
        hide: fieldsToHide.includes(col.colId),
      }));

      params.columnApi.applyColumnState({
        state: defaultState,
        applyOrder: true,
      });
    }
  };

  useEffect(() => {
    if (
      gridRef.current?.api &&
      gridData?.productList?.length &&
      gridData?.inputPayload?.filterJson
    ) {
      try {
        // Parse filterJson
        const filterJson = JSON.parse(gridData.inputPayload.filterJson);

        // Extract campaign IDs
        const campaignIds = filterJson?.campaignId?.conditions
          ? filterJson.campaignId.conditions.map((condition) => condition.filter)
          : filterJson?.campaignId?.filter
            ? [filterJson.campaignId.filter]
            : [];

        // Extract tag IDs
        const tagIds = filterJson?.tagIds?.conditions
          ? filterJson.tagIds.conditions.map((condition) => condition.filter)
          : filterJson?.tagIds?.filter
            ? [filterJson.tagIds.filter]
            : [];

        // Create a mapping of campaignId -> campaignName
        const campaignMapping = rowData.reduce((acc, campaign) => {
          acc[campaign.id] = campaign.campaignName;
          return acc;
        }, {});

        // Get campaign names from matched IDs
        const matchedCampaignNames = campaignIds.map(
          (id) => campaignMapping[id] || 'Unknown Campaign'
        );

        // Update contacts with campaignName and tags
        const updatedContacts = gridData.account.map((account) => ({
          ...account,
          campaignName: matchedCampaignNames, // Correct format
          // tags: matchedTagNames, // Flattened array
        }));

        // Update grid data
        const updatedGridData = {
          ...gridData,
          account: updatedContacts,
        };

        // Update the grid with new data
        gridRef.current.api.setGridOption('rowData', updatedGridData.account);
        gridRef.current.api.refreshCells();
      } catch (error) {
        console.error('Error parsing filterJson:', error);
      }
    }
  }, [gridData, rowData]);

  const filterDefaultSetRef = useRef(null);

  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
      } else {
        let filterModel = event.api.getFilterModel();

        // Create mappings for campaign names and tag names to their IDs
        const productMapping = {};
        if (rowData?.length) {
          rowData.forEach((item) => {
            productMapping[item.accountName] = item.id;
          });
        }

        const updatedFilterModel = Object.keys(filterModel).reduce((acc, dynamicFilterKey) => {
          let filterData = filterModel[dynamicFilterKey];

          if (dynamicFilterKey === 'campaignId') {
            // Convert campaign names to campaign IDs
            const idValues = filterData.values
              .map((name) => productMapping[name])
              .filter((id) => id !== undefined);

            if (idValues.length > 0) {
              acc[dynamicFilterKey] = {
                filterType: 'text',
                operator: 'OR',
                conditions: idValues.map((id) => ({
                  filterType: 'text',
                  type: 'contains',
                  filter: id, // Ensure we send ID, not name
                })),
              };
            }
          }

          // Keep Other Filters Unchanged
          else {
            acc[dynamicFilterKey] = filterData;
          }

          return acc;
        }, {});

        const payload = {
          ...gridData?.inputPayload,
          filterJson: JSON.stringify(updatedFilterModel),
          pageNumber: 1, // Reset to first page on filter change
        };

        dispatch(
          fetchDataAndUpdateStore({
            key: 'productList',
            inputData: payload,
          })
        );
      }
    },
    [dispatch, gridData?.inputPayload, rowData]
  );
  return (
    <div className="cutome_ag_theme">
      {loading && <Loader />}
      <div
        className={`ag-theme-${selectedTheme} tbl_height`}
        style={{ width: '100%', height: `100%` }}
      >
        <div ref={gridContainerRef} className="ag-grid-container">
          <AgGridReact
            ref={gridRef}
            domLayout="fixedHeight"
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            rowSelection="multiple"
            enableRangeSelection={true}
            suppressExcelExport={true}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            pivotPanelShow="always"
            sideBar={sideBar}
            popupParent={popupParent.current}
            sortingOrder={['asc', 'desc', null]}
            rowDragManaged={true}
            suppressMoveWhenRowDragging={true}
            rowGroupPanelShow="always"
            gridOptions={gridOptions}
            onFilterChanged={handleFilterChanged}
            onGridReady={handleGridReady}
          />
          {gridParams && (
            <FilterHandler
              params={gridParams}
              gridData={gridData}
              columnDefs={columnDefs}
              filterDefaultSetRef={filterDefaultSetRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
